import { MapboxOverlay } from '@deck.gl/mapbox';
import { LineLayer, PathLayer, PolygonLayer, TextLayer } from '@deck.gl/layers';
import { PathStyleExtension } from '@deck.gl/extensions';
import { distance as turfDistance } from '@turf/turf';
import chroma from "chroma-js";
import moment from 'moment';
import * as c from "../common.js";
import * as bd from "../domain/boat-data.js";
import * as mapDist from "./map-distance-tool.js";
import * as geom from "../utils/geometry-util.js";
import * as util from "../utils/utils.js";
import destination from '@turf/destination';
import { point } from '@turf/helpers';
import { SailTrack } from "../domain/Race.js";
import { TrackMapView } from "./TrackMapView.js";
import { distance } from '@turf/turf';
import { shortTimeFormat } from '../utils/utils.js';

export const SEPARATION_MODE = {
    WIND: "Wind",
    DIST: "Dist",
    WAYPOINT: "Waypoint"
};

const IS_MOBILE = window.matchMedia("(max-width: 768px)").matches;
const FULL_RES_TRACK_MINS = IS_MOBILE ? 30 : 60;
const MED_RES_TRACK_MINS = IS_MOBILE ? 60 : 180;

const WIND_BARB_MODES = {
    TRUE: "True",
    APPARENT: "Apparent",
    COLOR_SHIFT: "Shift",
    COLOR_PLAIN: "Plain"
};

export class TrackAnimator {
    constructor(map, race, mapView) {
        this.map = map;
        this.race = race;
        this.sailTracks = race.sail().getTracks();
        this.trackStyle = c.TRACK_STYLE.SOG;

        this.firstTsSeconds = race.sail().getFirstTsSeconds();
        this.lastTsSeconds = race.sail().getLastTsSeconds();
        this.playTsSeconds = this.firstTsSeconds;
        this.sailData = race.sail().data;
        this.sail = race.sail();
        this.previousFrameTs = null;

        // this.vesselCoords = null;
        this.postAnimateCallback = null;
        this.animateTracksHandler = this.animateTracks.bind(this);
        this.animationFrameId = null;

        this.uiDrawWindLines = false;
        this.uiWindBarbMode = WIND_BARB_MODES.TRUE;
        this.uiWindBarbColor = WIND_BARB_MODES.COLOR_PLAIN;
        this.windArrowAvgWindDir = null;
        this.showManeuvers = false;

        const SLOWEST_TIME_SKIP_RATE = 10;
        const FASTEST_TIME_SKIP_RATE = 40 * 60;
        this.showRecords = true;
        this.separationMode = SEPARATION_MODE.WIND;
        this.showSeparationModeGraphics = false;
        this.showTimeMode = false;
        this.timeSkipRate = SLOWEST_TIME_SKIP_RATE + (FASTEST_TIME_SKIP_RATE - SLOWEST_TIME_SKIP_RATE) / 2;
        this.mapView = mapView;

        // this.roundTheCansRace = false;
        const track = race.sail().getTracks()[0];
        // if (trackToExtentRatio(track)) {
        //     this.roundTheCansRace = true;
        //     console.log('Detected round the cans race in track: ', track.getShipName());
        // }
        this.trackToExtentRatio = trackToExtentRatio(track);
        console.log("Track to extent ratio:",this.trackToExtentRatio);
        // this.mapView.setViewMode(VIEW_MODE.NORTH_UP_FOLLOW);
        this._followedVessel = this.sail.getVesselTrack();

        // deck.gl overlay for tracks
        this.overlay = new MapboxOverlay({ layers: [] });
        map.addControl(this.overlay);

        this.initializeLayers();
    }

    getWindArrowAlpha(index, total, minAlpha = 0.1, exponent = 5) {
        if (total <= 1) return 1;
        const t = index / (total - 1);
        const bias = Math.pow(t, exponent);
        const a = minAlpha + (1 - minAlpha) * bias;
        return Math.min(Math.max(a, 0), 1);
    }

    //TODO why need 1- alpha?
    trackAlphaRamp(data, i, endIndex, minAlpha = 0.2) {
        if (data == null || endIndex >= data.length) return minAlpha;

        if (this.trackToExtentRatio > 1) {
            const maxRatio = 1 / this.trackToExtentRatio;
            const lengthRatio = (endIndex - i) / data.length;
            if (lengthRatio > maxRatio) {
                return 0;
            }
            return  1 - lengthRatio/maxRatio;
        }
        return 1;
    }

    setPostAnimateCallback(callback) {
        this.postAnimateCallback = callback;
    }

    getCurrentPlayTime() {
        return moment(this.playTsSeconds * 1000);
    }

    setCurrentPlayTime(ts) {
        const tsSeconds = ts/1000;
        if (this.firstTsSeconds > tsSeconds || this.lastTsSeconds < tsSeconds)
            return;
        this.playTsSeconds = tsSeconds;
        this.update();
    }

    start() {
        if (!c.isUIState("MAP", c.UI_STATE.MAP.PLAYING)) {
            c.setUIState("MAP", c.UI_STATE.MAP.READY_TO_PLAY);
            console.log(
                "startAnimation at:",
                new Date(this.playTsSeconds * 1000),
                "firstTsSeconds:",
                new Date(this.firstTsSeconds * 1000),
                "lastTsSeconds:",
                new Date(this.lastTsSeconds * 1000)
            );
            this.animateTracks();
        }
    }

    pause() {
        c.setUIState("MAP", c.UI_STATE.MAP.PAUSED);
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    destroy() {
        this.pause();
        if (this.overlay) {
            this.overlay.setProps({ layers: [] });
            this.map.removeControl(this.overlay);
            this.overlay = null;
        }
        this.map = null;
    }

    reset() {
        const startPoint = this.race.getStartPoint();
        this.pause();
        c.setUIState("MAP", c.UI_STATE.MAP.RESET);
        this.playTsSeconds = this.firstTsSeconds;
        if (startPoint != null) {
            this.playTsSeconds = startPoint.unix();
        } else {
            this.playTsSeconds = this.firstTsSeconds;
        }
    }

    advanceFrame(count) {
        const thisFrameTs = moment();
        if (count == undefined || count == null) {
            if (this.previousFrameTs) {
                this.playTsSeconds =
                    this.playTsSeconds + (thisFrameTs.diff(this.previousFrameTs, 'ms') / 1000) * this.timeSkipRate;
            }
        } else {
            const SLOWEST_TIME_SKIP_RATE = 10;
            this.playTsSeconds = this.playTsSeconds + this.timeSkipRate / SLOWEST_TIME_SKIP_RATE * count;
        }
        this.previousFrameTs = thisFrameTs;
        this.update();
    }

    animateTracks() {
        if (c.isUIState("MAP", c.UI_STATE.MAP.READY_TO_PLAY)) {
            this.previousFrameTs = moment();
            c.setUIState("MAP", c.UI_STATE.MAP.PLAYING);
        }

        if (c.isUIState("MAP", c.UI_STATE.MAP.PLAYING)) {
            this.advanceFrame();
        }

        if (this.playTsSeconds >= this.lastTsSeconds) {
            c.setUIState("MAP", c.UI_STATE.MAP.PAUSED);
        }

        if (c.isUIState("MAP", c.UI_STATE.MAP.PLAYING)) {
            this.animationFrameId = requestAnimationFrame(this.animateTracksHandler);
        }
    }

    
    setViewExtent() {
        this.mapView.setMapView(this._followedVessel?.getPlayFrame());
    }

    getTrackColor(previousFrame, frame) {
        if (this.race.getStartPoint() != null && frame.t.valueOf() < this.race.getStartPoint().valueOf())
            return c.TRACK_COLOR.HIDDEN;

        if ((frame.t.valueOf() - previousFrame.t.valueOf()) > 600000)
            return c.TRACK_COLOR.NO_DATA;

        let value = null;
        if (this.trackStyle == c.TRACK_STYLE.TWS) {
            value = this.sail.frameTws(frame);
        } else if (this.trackStyle == c.TRACK_STYLE.STW) {
            value = frame.bs;
        } else if (this.trackStyle == c.TRACK_STYLE.SOG) {
            value = frame.sog;
        } else if (this.trackStyle == c.TRACK_STYLE.CURRENT) {
            if (frame.sog != undefined && frame.sog != null && frame.bs != undefined && frame.bs != null) {
                value = frame.sog - frame.bs;
            }
        } else if (this.trackStyle == c.TRACK_STYLE.VMG) {
            value = frame._vmg;
        } else if (this.trackStyle == c.TRACK_STYLE.POLAR_PERF) {
            value = frame.calc?.polarPerf;
        }

        if (value != undefined && value != null)
            return this.trackStyle.colorMap(value).hex();

        return c.TRACK_COLOR.DEFAULT;
    }


    calculateSeparationLabel(track) {
        if (this.showTimeMode) {
            return this.calculateSeparationTime(track);
        }
        return this.calculateSeparationMeters(track);
    }

    calculateSeparationTime(track) {
        if (track.getType() == SailTrack.TYPE.VESSEL) return "0";
        if (this.separationMode == SEPARATION_MODE.DIST) return "--";
        const otherFrame = track.getPlayFrame();
        const vesselFrame = this.sail.getVesselTrack().getPlayFrame();
        const currentIdx = track.getPlayIndex();
        const currentSep = this.calcSeparationDistance(vesselFrame, otherFrame);

        if (currentSep == null) return "--";

        let separationTime = Infinity;
        const trackFrames = track.getFrames();
        for (let i = currentIdx + 1; i < trackFrames.length; i++) {
            if (this.isCrossing(currentSep, vesselFrame, trackFrames[i])) {
                separationTime = trackFrames[i].t.diff(otherFrame.t, 'seconds');
                break;
            }
        }
        for (let i = currentIdx - 1; i >= 0; i--) {
            if (this.isCrossing(currentSep, vesselFrame, trackFrames[i])) {
                const newSeparationTime = trackFrames[i].t.diff(otherFrame.t, 'seconds');
                if (Math.abs(newSeparationTime) < Math.abs(separationTime)) {
                    separationTime = newSeparationTime
                }
                break;
            }
        }
        if (separationTime == Infinity) return "--";

        return shortTimeFormat(separationTime);
    }

    isCrossing(currentSep, vesselFrame, currentShipFrame) {
        const sep = this.calcSeparationDistance(vesselFrame, currentShipFrame);
        if (sep != null && Math.sign(sep) !== Math.sign(currentSep)) {
            return this.absCOGDiff(currentShipFrame._cog, vesselFrame._cog) < 90;
        }
        return false;
    }

    absCOGDiff(otherCOG, vesselCOG) {
        if (otherCOG == null || vesselCOG == null) return false;
        return Math.abs(((otherCOG - vesselCOG + 540) % 360) - 180);
    }

    calcSeparationDistance(vesselFrame, otherFrame) {
        if (this.separationMode == SEPARATION_MODE.WIND) {
            return this.calcSeparationOverAxis(vesselFrame, otherFrame, this.sail.frameTwdAvgLong(vesselFrame));
        } else if (this.separationMode == SEPARATION_MODE.DIST) {
            return turfDistance([vesselFrame.lon, vesselFrame.lat], [otherFrame.lon, otherFrame.lat], { units: 'meters' });
        } else if (this.separationMode == SEPARATION_MODE.WAYPOINT) {
            const coord = mapDist.getWayPointCoord();
            if (!coord) return null;
            const wayPt = coord;
            const vesselDist = turfDistance([vesselFrame.lon, vesselFrame.lat], wayPt, { units: 'meters' });
            const otherDist = turfDistance([otherFrame.lon, otherFrame.lat], wayPt, { units: 'meters' });
            return vesselDist - otherDist;
        }
        return null;
    }

    calculateSeparationMeters(track) {
        let distM = "-";
        if (track.getType() == SailTrack.TYPE.VESSEL) {
            distM = "0 m";
        } else {
            const otherFrame = track.getPlayFrame();
            if (this.getCurrentPlayTime().diff(otherFrame.t, 'minutes') < 5) {
                const vesselFrame = this.sail.getVesselTrack().getPlayFrame();
                distM = this.calcSeparationDistance(vesselFrame, otherFrame);
                if (distM == null) return "-";
                if (distM != "-" && Math.abs(distM) > 1000) {
                    distM = `${parseFloat(distM / 1000).toFixed(1)}km`;
                } else {
                    distM = `${parseInt(parseFloat(distM / 10).toFixed(0) * 10)}m`;
                }
            }
        }
        return distM;
    }

    calcSeparationOverAxis(vesselFrame, otherFrame, axisDeg) {
        if (axisDeg != null) {
            const lat1 = vesselFrame.lat;
            const lon1 = vesselFrame.lon;
            const lat2 = otherFrame.lat;
            const lon2 = otherFrame.lon;
            const distance = turfDistance([lon1, lat1], [lon2, lat2], { units: 'meters' });
            const bearing = bd.getBearingLonLat(lon1, lat1, lon2, lat2);
            const windHeading = (axisDeg) % 360;
            return bd.projectVector(bearing, distance, windHeading);
        }
        return NaN;
    }

    showRecordsPopup(record, currentFrame) {
        const tsWithinSec = (frame, rec, field, sec) => {
            if (rec[field] != null)
                return Math.abs(frame.t.diff(rec[field].t, 'seconds')) < sec;
            return false;
        };

        if (tsWithinSec(currentFrame, record, "maxBs", 30)) {
            this.showPopupAtCoord(currentFrame, `Max Speed Through Water: ${record.maxBs.bs}`);
        }
        if (tsWithinSec(currentFrame, record, "maxTws", 30)) {
            this.showPopupAtCoord(currentFrame, `Max Wind Speed: ${record.maxTws.tws}`);
        }
    }

    showPopupAtCoord(frame, htmlContent) {
        const popup = document.getElementById('map-popup');
        const pixel = this.map.project([frame.lon, frame.lat, frame.t.unix()]);

        popup.style.left = `${pixel.x}px`;
        popup.style.top = `${pixel.y}px`;
        popup.style.display = 'block';

        if (htmlContent) {
            popup.querySelector('.map-popup-content').innerHTML = htmlContent;
        }
        setTimeout(() => {
            document.getElementById('map-popup').style.display = 'none';
        }, 1000);
    }

    setWindDrawMode(enabled) {
        this.uiDrawWindLines = enabled;
        this.update();
    }

    setWindBarbMode(mode) {
        this.uiWindBarbMode = mode;
        this.update();
    }

    setWindBarbColor(mode) {
        this.uiWindBarbColor = mode;
        this.update();
    }

    setSeparationMode(mode) {
        this.separationMode = mode;
        this.update();
    }

    setSeparationModeGraphics(enabled) {
        this.showSeparationModeGraphics = enabled;
        this.update();
    }

    setShowRecords(enabled) {
        this.showRecords = enabled;
    }

    setShowTimeMode(enabled) {
        this.showTimeMode = enabled;
        this.update();
    }

    setTimeSkipRate(rate) {
        this.timeSkipRate = rate;
    }

    getTimeSkipRate() {
        return this.timeSkipRate;
    }

    setRoundTheCansRace(enabled) {
        this.roundTheCansRace = enabled;
    }

    setShowManeuvers(enabled) {
        this.showManeuvers = enabled;
        this.update();
    }

    // setViewMode(mode) {
    //     this.mapView.setViewMode(mode);
    // }

    // getViewMode() {
    //     return this.mapView.getViewMode();
    // }

    setFollowedVessel(track) {
        this._followedVessel = track;
    }

    getFollowedVessel() {
        return this._followedVessel;
    }

    setMapBearingOffset(offset) {
        this.mapView.setMapBearingOffset(offset);
    }

    setAutoCenterAllowed(flag) {
        this.mapView.setAutoCenterAllowed(flag);
    }

    addMapBearingOffset(offset) {
        this.mapView.addMapBearingOffset(offset);
    }

    isTrackVisible(timestampSeconds) {
        if (timestampSeconds > this.playTsSeconds) return false;
        const minVisibleTs = this.playTsSeconds - (MED_RES_TRACK_MINS * 60);
        return timestampSeconds >= minVisibleTs;
    }

    initializeLayers() {
        // const vesselData = this.sailData.vessel;
        // if (!this.vesselCoords) {
        //     this.vesselCoords = [];
        //     for (let i = 0; i < vesselData.data.length; i++) {
        //         const f = vesselData.data[i];
        //         this.vesselCoords.push([f.lon, f.lat, f.t.unix()]);
        //     }
        // }

        this.trackPaths = [];
        this.sailTracks.forEach(sailTrack => {
            const frames = sailTrack.getFrames();

            const path = [];
            const colors = [];

            for (let i = 0; i < frames.length; i++) {
                const frame = frames[i];
                path.push([frame.lon, frame.lat]);

                if (i > 0) {
                    const color = this.getTrackColor(frames[i - 1], frame);
                    colors.push(hexToRgb(color));
                } else {
                    colors.push([128, 128, 128]);
                }
            }

            // console.log('initializeLayers - track:', sailTrack.getShipName(), 'frames:', frames.length, 'paths:', path.length, 'colors:', colors.length);
            // if (frames.some(f => !f.sog && !f.bs)) {
            //     console.log('initializeLayers - found frame missing sog/bs:', frames.find(f => !f.sog && !f.bs));
            // }

            this.trackPaths.push({
                path: path,
                colors: colors,
                timestamps: frames.map(f => f.t.unix())
            });
        });
    }

    setTrackStyle(trackStyle) {
        this.trackStyle = trackStyle;
        this.initializeLayers();
        this.update();
    }

    update() {
        if (!this.map || !this.overlay) {
            return;
        }

        this.race.sail().updatePlayIndices(this.playTsSeconds);
        
        const vesselTrack = this.sail.getVesselTrack();
        const vesselFrame = vesselTrack.getPlayFrame();
        const records = vesselTrack.ship.records;
        this.setViewExtent();
        if (this.showRecords && records) {
            this.showRecordsPopup(records, vesselFrame);
        }
        const visible = vesselTrack.isVisible()
        const visiblePaths = this.createBoatPaths();
        const windLineData = visible ? this.createWindDirLine(vesselFrame) : [];
        const windBarbsData = visible ? this.calculateWindBarbs(vesselTrack) : [];
        const separationGraphicsData = visible ? this.createSeparationGraphics(vesselFrame) : [];
        const maneuversData = visible ? this.createManeuvers(vesselFrame) : [];
        
        const separationLabelsData = this.createSeparationLabels();
        const boatMarkersData = this.createBoatMarkersLineData();

        this.overlay.setProps({
            layers: [
                new PathLayer({
                    id: 'separation-graphics',
                    data: separationGraphicsData,
                    getPath: d => d.path,
                    getColor: [128, 128, 128, 255],
                    getWidth: 2,
                    widthMinPixels: 2,
                    getDashArray: [10, 5],
                    dashJustified: true,
                    extensions: [new PathStyleExtension({ dash: true, highPrecisionDash: true })]
                }),
                new PathLayer({
                    id: 'track-paths',
                    data: visiblePaths,
                    getPath: d => d.path,
                    getColor: d => d.color,
                    getWidth: 3,
                    widthMinPixels: 2,
                    capRounded: true,
                    jointRounded: true,
                    parameters: { depthTest: false }
                }),
                new LineLayer({
                    id: 'wind-direction',
                    data: windLineData,
                    getSourcePosition: d => d.sourcePosition,
                    getTargetPosition: d => d.targetPosition,
                    getColor: [0, 0, 0, 128],
                    getWidth: 2,
                    widthMinPixels: 2
                }),
                new LineLayer({
                    id: 'wind-barbs',
                    data: windBarbsData,
                    getSourcePosition: d => d.sourcePosition,
                    getTargetPosition: d => d.targetPosition,
                    getColor: d => d.color,
                    getWidth: 2
                }),
                new PolygonLayer({
                    id: 'boat-markers',
                    data: boatMarkersData,
                    getPolygon: d => d.polygon,
                    getFillColor: d => d.color,
                    getLineColor: d => d.color,
                    getLineWidth: 2,
                    filled: true,
                    stroked: true
                }),
                new TextLayer({
                    id: 'separation-labels',
                    data: separationLabelsData,
                    getPosition: d => d.position,
                    getText: d => d.text,
                    getSize: d => d.size,
                    getColor: [0, 0, 0, 255],
                    outlineColor: [255, 255, 255, 255],
                    outlineWidth: 2,
                    getTextAnchor: 'middle',
                    getAlignmentBaseline: 'center'
                }),
                new LineLayer({
                    id: 'maneuver-lines',
                    data: maneuversData.lines,
                    getSourcePosition: d => d.sourcePosition,
                    getTargetPosition: d => d.targetPosition,
                    getColor: d => d.color,
                    getWidth: d => d.width,
                    widthMinPixels: 2
                }),
                new TextLayer({
                    id: 'maneuver-labels',
                    data: maneuversData.labels,
                    getPosition: d => d.position,
                    getText: d => d.text,
                    getSize: 12,
                    getColor: [0, 0, 0, 255],
                    outlineColor: [255, 255, 255, 255],
                    outlineWidth: 2,
                    getTextAnchor: 'middle',
                    getAlignmentBaseline: 'center'
                })
            ]
        });

        if (this.postAnimateCallback) {
            this.postAnimateCallback();
        }
    }

    createBoatPaths() {
        const visiblePaths = [];
        
        this.trackPaths.forEach((trackPath, idx) => {
            const sailTrack = this.sailTracks[idx];
            if (!sailTrack.isVisible()) return;
            const frames = sailTrack.getFrames();
            
            const endIndex = sailTrack.getPlayIndex();
            if (endIndex <= 1) return;

            let currentColor = trackPath.colors[0];
            let segmentStart = 0;

            //TODO for some reason this code was written to run short of end index.
            // later fix the gap in the track cause by this
            for (let i = 0; i < endIndex; i++) {
                const color = trackPath.colors[i];
                if (color == null) {
                    console.log("was null");
                    //TODO remove
                }
                
                const colorChanged = color[0] !== currentColor[0] || color[1] !== currentColor[1] || color[2] !== currentColor[2];
                const segmentTooLong = i - segmentStart > 200;
                const needNewSegment = colorChanged || segmentTooLong;

                if (needNewSegment || i === endIndex - 1 || segmentTooLong) {
                    const segmentEnd = needNewSegment ? i + 1 : endIndex;
                    const alpha = this.trackAlphaRamp(frames, i, endIndex);

                    visiblePaths.push({
                        path: trackPath.path.slice(segmentStart, segmentEnd),
                        color: [...currentColor, alpha * 255]
                    });

                    if (needNewSegment) {
                        currentColor = color;
                        segmentStart = i;
                    }
                }
            }
        });
        return visiblePaths;
    }

    createWindDirLine(vesselFrame) {
        const windLineData = [];
        const scale = geom.getMinSizePixels(this.map, vesselFrame.lat, 4);
        const endPos = this.calculateWindBarbEndModes(vesselFrame, scale);
        if (endPos != null) {
            windLineData.push({
                sourcePosition: [vesselFrame.lon, vesselFrame.lat],
                targetPosition: endPos
            });
        }
        return windLineData;
    }

    calculateWindBarbs(vesselTrack) {
        if (!this.uiDrawWindLines) return [];
        const playIndex = vesselTrack.getPlayIndex();

        const windBarbsData = [];
        const zoom = this.map.getZoom();
        const skipFactor = Math.max(1, Math.pow(2, Math.floor((16 - zoom))));
        const frames = vesselTrack.getFrames();
        console.log(zoom," "+skipFactor);
        let lastWindLineTs = this.firstTsSeconds;
        const lat = vesselTrack.getPlayFrame().lat;
        const windArrowSteps = geom.getMaxMinSizePixels(this.map, lat, 3, 2) * 3;
        const scale = geom.getMinSizePixels(this.map, lat, 4);
        let barbCounter = 0;

        for (let i = 0; i < playIndex; i++) {
            const currentFrame = frames[i];

            if (this.sail.frameTwa(currentFrame) != null) {
                const nextWindTs = currentFrame.t.unix();
                if (nextWindTs - windArrowSteps > lastWindLineTs) {
                    lastWindLineTs = nextWindTs;

                    if (barbCounter % skipFactor === 0 && playIndex - i < 5000) {
                        const alpha = this.getWindArrowAlpha(i, playIndex);
                        const color = chroma('grey').alpha(alpha).rgba();
                        // TODO either remove shift color button or add this functionality back in
                        // TODO remove duplication by including alpha and return data struct insead of pos
                        const endPos = this.calculateWindBarbEndModes(currentFrame, scale);
                        if (endPos != null) {
                            windBarbsData.push({
                                sourcePosition: [currentFrame.lon, currentFrame.lat],
                                targetPosition: endPos,
                                color: [Math.round(color[0]), Math.round(color[1]), Math.round(color[2]), Math.round(color[3] * 255)]
                            });
                        }
                    }

                    barbCounter++;
                }
            }
        }
        return windBarbsData;
    }

    calculateWindBarbEndModes(currentFrame, mapScale) {
        const position = [currentFrame.lon, currentFrame.lat];
        const directionDegrees = this.uiWindBarbMode == WIND_BARB_MODES.APPARENT
            ? this.sail.frameAwdAvgShort(currentFrame)
            : this.sail.frameTwdAvgShort(currentFrame);
        const windSpeed = this.uiWindBarbMode == WIND_BARB_MODES.APPARENT
            ? this.sail.frameAwsAvgShort(currentFrame)
            : this.sail.frameTwsAvgShort(currentFrame);

        if (directionDegrees == null || windSpeed == null)
            return null;
        else
            return this.calculateWindBarbEnd(position, directionDegrees, windSpeed, mapScale);
    }

    calculateWindBarbEnd(position, directionDegrees, windSpeed, mapScale) {
        const lengthMeters = windSpeed * mapScale * 2;
        const endPoint = destination(
            point(position),
            lengthMeters / 1000,
            directionDegrees,
            { units: 'kilometers' }
        );
        return endPoint.geometry.coordinates;
    }

    createBoatMarkersLineData() {
        const markers = [];
        this.sailTracks.forEach(sailTrack => {
            const playIndex = sailTrack.getPlayIndex();
            if (!sailTrack.isVisible() || playIndex == null) return;
            const frame = sailTrack.getFrames()[sailTrack.getPlayIndex()];
            if (frame === undefined || frame == null || frame.lon == null || frame.lat == null) {
                console.error("frame was null in createBoatMarkersLineData "+frame+" index"+playIndex);
                return;
            }
            const angle = this.sail.frameCog(frame);
            const color = hexToRgb(sailTrack.getColor());
            const position = [frame.lon, frame.lat];
            const sizeMeters = geom.getMaxMinSizeMeters(this.map, frame.lat, 3, 2) * 3;

            const tip = this.boatTip(frame);
            const left = destination(point(position), sizeMeters * 0.6 / 1000, angle + 120, { units: 'kilometers' });
            const right = destination(point(position), sizeMeters * 0.6 / 1000, angle - 120, { units: 'kilometers' });

            markers.push({
                polygon: [tip.geometry.coordinates, left.geometry.coordinates, right.geometry.coordinates],
                color: [...color, 255]
            });
        });
        return markers;
    }

    boatTip(frame) {
        const angle = this.sail.frameCog(frame);
        const position = [frame.lon, frame.lat];
        const sizeMeters = geom.getMaxMinSizeMeters(this.map, frame.lat, 3, 2) * 3;
        return destination(point(position), sizeMeters / 1000, angle, { units: 'kilometers' });
    }

    createSeparationLabels() {
        const labels = [];
        this.sailTracks.forEach(sailTrack => {
            if (!sailTrack.isVisible() || sailTrack.getPlayIndex() == null) return;
            if (sailTrack.getType() == SailTrack.TYPE.VESSEL || !this.showSeparationModeGraphics) return;

            const frame = sailTrack.getPlayFrame();
            const angle = this.sail.frameCog(frame);
            const metersPerPixel = geom.getMetersPerPixel(this.map, frame.lat);
            const sizePixels = geom.getMaxMinSizePixels(this.map, frame.lat, 3, 2) * 3;
            const offsetMeters = sizePixels * metersPerPixel * 2;

            const labelPos = destination(
                point([frame.lon, frame.lat]),
                offsetMeters / 1000,
                angle,
                { units: 'kilometers' }
            );

            labels.push({
                position: labelPos.geometry.coordinates,
                text: this.calculateSeparationLabel(sailTrack),
                size: sizePixels * 2
            });
        });
        return labels;
    }

    createSeparationGraphics(vesselFrame) {
        if (!this.showSeparationModeGraphics) return [];


        if (this.separationMode == SEPARATION_MODE.WIND) {
            const boat = this.boatTip(vesselFrame).geometry.coordinates;
            return this.createLadderLinePerpendicular(boat, this.sail.frameTwdAvgLong(vesselFrame));
        } else if (this.separationMode == SEPARATION_MODE.WAYPOINT) {
            const boat = [vesselFrame.lon, vesselFrame.lat];
            return this.createLadderLineArc(boat);
        }
        return [];
    }

    createLadderLinePerpendicular(position, directionDegrees) {
        const lengthKm = geom.sizeMetersWithBounds(this.map, 4000, 64000, 1 / 40, position[1]) / 1000;
        const angle = directionDegrees - 90;

        const start = destination(point(position), lengthKm, angle, { units: 'kilometers' });
        const end = destination(point(position), lengthKm, angle + 180, { units: 'kilometers' });

        return [{ path: [start.geometry.coordinates, end.geometry.coordinates] }];
    }

    createLadderLineArc(vesselPos) {
        const wayPt = mapDist.getWayPointCoord();
        if (!wayPt) return [];

        const centerLonLat = wayPt;
        const radius = turfDistance(centerLonLat, vesselPos, { units: 'kilometers' });
        const bearing = bd.getBearingLonLat(centerLonLat[0], centerLonLat[1], vesselPos[0], vesselPos[1]);
        const arcCoords = [];
        for (let b = bearing - 45; b <= bearing + 45; b += 5) {
            const arcPoint = destination(point(centerLonLat), radius, b, { units: 'kilometers' });
            arcCoords.push(arcPoint.geometry.coordinates);
        }

        return [{ path: arcCoords }];
    }

    createManeuvers(frame) {
        const maneuversList = this.sail.getManeuversList();
        if (!this.showManeuvers || maneuversList == null) {
            return { lines: [], labels: [] };
        }

        const lines = [];
        const labels = [];
        //const maneuvers = this.sail.maneuvers.list;
        // const frame = this.sailData.vessel.data[frameIndex];
        const currentTime = frame.t;

        for (let i = maneuversList.length - 1; i >= 0; i--) {
            const m = maneuversList[i];
            if (currentTime.isAfter(m.completionFrame.t)) {
                const angles = m.getCalloutAnglesDeg();

                this.addManeuverSegment(
                    lines, labels,
                    [m.startFrame.lon, m.startFrame.lat],
                    [m.crossingFrame.lon, m.crossingFrame.lat],
                    angles[0], angles[1], m.startToCrossingTimeLost, frame.lat
                );
                this.addManeuverSegment(
                    lines, labels,
                    [m.crossingFrame.lon, m.crossingFrame.lat],
                    [m.completionFrame.lon, m.completionFrame.lat],
                    angles[1], angles[2], m.crossingToCompletionTimeLost, frame.lat
                );
                break;
            }
        }

        return { lines, labels };
    }

    addManeuverSegment(lines, labels, startCoord, endCoord, startAngle, endAngle, timeLost, lat) {
        const lineLength = geom.sizeMetersWithBounds(this.map, 160, 16000, 1 / 40, lat);
        const startEnd = destination(point(startCoord), lineLength / 1000, startAngle, { units: 'kilometers' });
        const endEnd = destination(point(endCoord), lineLength / 1000, endAngle, { units: 'kilometers' });

        lines.push({
            sourcePosition: startCoord,
            targetPosition: startEnd.geometry.coordinates,
            color: [128, 128, 128, 38],
            width: 2
        });

        lines.push({
            sourcePosition: endCoord,
            targetPosition: endEnd.geometry.coordinates,
            color: [128, 128, 128, 38],
            width: 2
        });

        let connectColor = [128, 128, 128, 102];
        if (timeLost != null && !isNaN(timeLost)) {
            connectColor = timeLost > 0 ? [255, 0, 0, 102] : [0, 255, 0, 102];
        }

        lines.push({
            sourcePosition: startEnd.geometry.coordinates,
            targetPosition: endEnd.geometry.coordinates,
            color: connectColor,
            width: 4
        });

        if (timeLost != null && !isNaN(timeLost)) {
            const offSetLength = lineLength / 6;
            const midLon = (startEnd.geometry.coordinates[0] + endEnd.geometry.coordinates[0]) / 2;
            const midLat = (startEnd.geometry.coordinates[1] + endEnd.geometry.coordinates[1]) / 2;
            const labelOffset = destination(point([midLon, midLat]), offSetLength / 1000, endAngle, { units: 'kilometers' });

            labels.push({
                position: labelOffset.geometry.coordinates,
                text: `${timeLost}s`
            });
        }
    }

}


function hexToRgb(hex) {
    return chroma(hex).rgb();
}

function hexToRgba(hex) {
    return chroma(hex).rgba();
}

export function trackToExtentRatio(track) {
    const frames = track.getFrames();
    if (!frames || frames.length < 2) return false;

    let minLon = Infinity, maxLon = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    frames.forEach(f => {
        minLon = Math.min(minLon, f.lon);
        maxLon = Math.max(maxLon, f.lon);
        minLat = Math.min(minLat, f.lat);
        maxLat = Math.max(maxLat, f.lat);
    });

    const width = distance([minLon, minLat], [maxLon, minLat], { units: 'meters' });
    const height = distance([minLon, minLat], [minLon, maxLat], { units: 'meters' });

    let lineLength = 0;
    for (let i = 1; i < frames.length; i++) {
        lineLength += distance([frames[i - 1].lon, frames[i - 1].lat], [frames[i].lon, frames[i].lat], { units: 'meters' });
    }

    return lineLength / (width+height);
}